/* Fast URL for webOS 3 (Chromium 38) and every other legacy-bundle browser.

   core-js's `web.url` (installed by the legacy polyfills bundle because Chrome
   38's native URL has no `searchParams`) REPLACES the global URL with a pure-JS
   WHATWG state machine. Measured on the TV (LG webOS 3.9, Chromium 38, during
   live playback): 72.9ms per `new URL()` vs 0.05ms for the native constructor
   (still reachable as `webkitURL`) — ~1460x, same href/hostname/search/pathname.
   shaka calls `new URL()` for EVERY segment request (hostname) and every
   manifest BaseURL resolution, and the RUM XHR instrumentation for every API
   call: ~26% of the main thread during playback of a big live manifest.

   Installs a native-backed URL: parsing and reads use the native object; the
   instance PROMOTES itself to a core-js URL (and stays there) on any setter,
   so mutation semantics stay core-js's. `searchParams` does NOT promote: it is
   a core-js URLSearchParams parsed from the native `search` (7ms vs 270ms for
   the promotion on a 1.6 KB manifest URL — `new URL(u).searchParams.get(k)` is
   the common read, and promotions were ~1.5s of every channel join); its
   mutators re-parse the native URL with the new query (no promotion either).
   Inputs that are not plain printable ASCII (spaces, backslashes, IDN hosts,
   non-ASCII paths/hashes — where Chrome 38 and the spec disagree) go straight
   to core-js. Statics (createObjectURL/revokeObjectURL/canParse/…) are copied
   from the core-js constructor. Leaves a fully native URL (modern bundle, no
   core-js) untouched. Imported from src/index.jsx before the app modules. */

/* eslint-disable no-underscore-dangle -- private slots on a hand-rolled class */
/* eslint-disable func-names -- ES5 constructor function: the installed URL must
   be `new`-able and keep its own prototype */

const NativeURL = typeof window === 'undefined' ? undefined : window.webkitURL;
const CoreURL = typeof window === 'undefined' ? undefined : window.URL;

/* Printable ASCII except backslash (0x5C): the inputs both parsers agree on. */
const NATIVE_SAFE = /^[!-[\]-~]*$/;

const READ_KEYS = [
  'href', 'origin', 'protocol', 'username', 'password',
  'host', 'hostname', 'port', 'pathname', 'search', 'hash',
];
const WRITE_KEYS = new Set([
  'href', 'protocol', 'username', 'password',
  'host', 'hostname', 'port', 'pathname', 'search', 'hash',
]);
const SKIPPED_STATICS = new Set(['prototype', 'length', 'name', 'arguments', 'caller']);
const CoreSearchParams = typeof window === 'undefined' ? undefined : window.URLSearchParams;
const SEARCH_MUTATORS = ['append', 'delete', 'set', 'sort'];

function nativeWorks() {
  try {
    return typeof NativeURL === 'function'
      && new NativeURL('b?a=1#h', 'http://x.y/c/d').href === 'http://x.y/c/b?a=1#h'
      && new NativeURL('http://x.y:8080/p').hostname === 'x.y';
  } catch {
    return false;
  }
}

const shouldInstall = typeof CoreURL === 'function'
  && NativeURL !== CoreURL
  && !CoreURL.__dtvFastUrl
  && nativeWorks();

if (shouldInstall) {
  const FastURL = function URL(url, base) {
    if (!(this instanceof FastURL)) {
      throw new TypeError("Failed to construct 'URL': Please use the 'new' operator");
    }
    const href = String(url);
    const baseHref = base === undefined ? undefined : String(base);
    if (NATIVE_SAFE.test(href) && (baseHref === undefined || NATIVE_SAFE.test(baseHref))) {
      try {
        this._native = baseHref === undefined
          ? new NativeURL(href)
          : new NativeURL(href, baseHref);
      } catch {
        /* Native throws a DOMException SyntaxError; the spec (and core-js) a TypeError. */
        throw new TypeError("Failed to construct 'URL': Invalid URL");
      }
      this._core = undefined;
      this._params = undefined;
    } else {
      this._native = undefined;
      this._params = undefined;
      this._core = baseHref === undefined ? new CoreURL(href) : new CoreURL(href, baseHref);
    }
  };

  /* Switch this instance to core-js for good (searchParams / mutation). */
  const promote = function (self) {
    if (!self._core) {
      // eslint-disable-next-line no-param-reassign -- the instance owns its slots
      self._core = new CoreURL(self._native.href);
      // eslint-disable-next-line no-param-reassign -- the instance owns its slots
      self._native = undefined;
    }
    return self._core;
  };

  const proto = FastURL.prototype;
  for (const key of READ_KEYS) {
    const descriptor = {
      configurable: true,
      enumerable: true,
      get() {
        return this._core ? this._core[key] : this._native[key];
      },
    };
    if (WRITE_KEYS.has(key)) {
      descriptor.set = function (value) {
        promote(this)[key] = value;
      };
    }
    Object.defineProperty(proto, key, descriptor);
  }
  /* The spec's URLSearchParams "update steps": an empty serialization drops the
     `?`. A native instance is re-parsed natively with the new query (the
     tokenization filter sets `hdntl` on EVERY manifest/segment request: promoting
     there was ~9% of steady playback on a 1h channel); anything else promotes. */
  const writeSearch = function (self, query) {
    if (!self._core) {
      const { href } = self._native;
      const hashAt = href.indexOf('#');
      const beforeHash = hashAt === -1 ? href : href.slice(0, hashAt);
      const queryAt = beforeHash.indexOf('?');
      const next = (queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt))
        + (query ? `?${query}` : '')
        + (hashAt === -1 ? '' : href.slice(hashAt));
      if (NATIVE_SAFE.test(next)) {
        // eslint-disable-next-line no-param-reassign -- the instance owns its slots
        self._native = new NativeURL(next);
        return;
      }
    }
    promote(self).search = query;
  };
  /* Parsed once per instance from the native search. Mutating it writes the
     serialization back: the URL follows its searchParams, as the spec links them.
     Once promoted, the core-js URL's own object is used. */
  const linkedSearchParams = function (self) {
    const params = new CoreSearchParams(self._native.search);
    for (const name of SEARCH_MUTATORS) {
      const mutate = params[name];
      if (typeof mutate === 'function') {
        params[name] = function () {
          // eslint-disable-next-line prefer-rest-params -- forward the call untouched
          const result = mutate.apply(params, arguments);
          writeSearch(self, params.toString());
          return result;
        };
      }
    }
    return params;
  };
  Object.defineProperty(proto, 'searchParams', {
    configurable: true,
    enumerable: true,
    get() {
      if (this._core || typeof CoreSearchParams !== 'function') return promote(this).searchParams;
      if (!this._params) this._params = linkedSearchParams(this);
      return this._params;
    },
  });
  proto.toString = function () {
    return this.href;
  };
  proto.toJSON = function () {
    return this.href;
  };
  if (typeof Symbol === 'function' && Symbol.toStringTag) {
    Object.defineProperty(proto, Symbol.toStringTag, { configurable: true, value: 'URL' });
  }

  /* createObjectURL/revokeObjectURL (MSE needs them), canParse, parse, … */
  for (const name of Object.getOwnPropertyNames(CoreURL)) {
    const descriptor = SKIPPED_STATICS.has(name)
      ? undefined
      : Object.getOwnPropertyDescriptor(CoreURL, name);
    if (descriptor) Object.defineProperty(FastURL, name, descriptor);
  }
  FastURL.__dtvFastUrl = true;

  window.URL = FastURL;
}

/* eslint-enable func-names -- end of the ES5 constructor block */
/* eslint-enable no-underscore-dangle -- end of the private-slot block */
